import { api } from '@goaltree/backend/convex/_generated/api'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { useEffect } from 'react'
import { z } from 'zod'

export const Route = createFileRoute('/_authenticated/action/follow')({
  validateSearch: z.object({
    handle: z.string(),
    back: z.string().optional(),
    action: z.enum(['follow', 'unfollow']).default('follow'),
  }),
  component: RouteComponent,
})

// Only send people back to our own public pages, never an arbitrary URL.
const BACK_HOSTS = ['gt.today', 'goaltree.ai', 'www.goaltree.ai']
function safeBack(back: string | undefined) {
  try {
    const u = new URL(back ?? '')
    return u.protocol === 'https:' && BACK_HOSTS.includes(u.hostname) ? u.toString() : null
  } catch {
    return null
  }
}

function RouteComponent() {
  const { handle, back, action } = Route.useSearch()
  const followMutation = useMutation(api.follows.follow)
  const myHandle = useQuery(api.preferences.getMyHandle)

  useEffect(() => {
    if (myHandle === undefined) return // still loading

    async function run() {
      const returnUrl = safeBack(back) ?? `https://gt.today/${handle}`

      // Self-profile — redirect back with _me=1 so the page hides the Follow button
      if (myHandle === handle) {
        const sep = returnUrl.includes('?') ? '&' : '?'
        window.location.href = `${returnUrl}${sep}_me=1&_v=${encodeURIComponent(handle)}`
        return
      }

      let following = action === 'follow'
      try {
        following = (await followMutation({ handle, want: action === 'follow' })).following
      } catch {}

      const separator = returnUrl.includes('?') ? '&' : '?'
      // _v tells gt.today who is looking, so later visits can read the real follow state.
      const viewer = myHandle ? `&_v=${encodeURIComponent(myHandle)}` : ''
      window.location.href = `${returnUrl}${separator}${following ? '_gtf=1' : '_gtu=1'}${viewer}`
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myHandle])

  return (
    <div className="flex h-svh items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  )
}
